/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { LaravelModelsEnum } from '@app/shared/models/model.model';
import { AuthenticationService, Logger } from '@app/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CommentsService } from '@app/shared/components/comments/comments-service.service';
import { CommentModel } from '@app/shared/components/comments/comment.model';
import { TypesService } from '@app/settings/types/types.service';
import { ApiQuery } from '@app/core/http/api-query';
import { Type } from '@app/settings/types/types.model';
import { ToastContainerDirective, ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { PaginatedResponse } from '@app/shared/models/paginated-response.model';
import { UserRoleType } from '@app/settings/users/user-role-type.enum';
import { UserRole } from '@app/settings/users/roles/user-role.model';
import { CommentsTypes } from '@app/settings/types/comments.enum';
import { NgScrollbar } from 'ngx-scrollbar';
// import { smooo}

const log = new Logger('CommentsComponent');

@Component({
  selector: 'app-comments',
  templateUrl: './comments.component.html',
  styleUrls: ['./comments.component.scss'],
})
export class CommentsComponent implements OnInit, OnDestroy {
  @Input() subjectType: LaravelModelsEnum;
  @Input() subjectId: number;
  form: FormGroup;
  formSearch: FormGroup;
  types: Array<Type>;
  commentsResponse: PaginatedResponse = new PaginatedResponse();
  @ViewChild(ToastContainerDirective, { static: true }) toastContainer: ToastContainerDirective;
  public usersTyping: Array<Object> = [];
  UserRoleType = UserRoleType;
  isCurrentUserAdministrator = false;
  LaravelModelsEnum = LaravelModelsEnum;
  showSearch = false;
  selectedSearchType: string | null = null;
  CommentsTypes = CommentsTypes;
  commentsExpanded = false;
  commentDefaultColor = '#646774';

  @ViewChild('perfectScroll', { static: true }) perfectScroll: NgScrollbar;

  // uncomment this to make whispers work start
  //  private keyUp: Subject<KeyboardEvent> = new Subject();
  //  private usersTypingTimeout = setTimeout(() => {}, 1);
  //  private websocketCommentChannel: PusherPrivateChannel;
  // uncomment this to make whispers work end

  constructor(
    private formBuilder: FormBuilder,
    private commentsService: CommentsService,
    private typesService: TypesService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private auth: AuthenticationService
  ) {
    this.toastr.overlayContainer = this.toastContainer;
  }

  ngOnDestroy() {
    log.debug('ngOnDestroy');
    // uncomment this to make whispers work start
    // if (this.websocketCommentChannel) {
    //   this.websocketCommentChannel.unsubscribe();
    // }
    // uncomment this to make whispers work end
  }

  ngOnInit() {
    this.commentsService.hasValue = false;
    this.checkRequiredFields(this.subjectType);
    this.checkRequiredFields(this.subjectId);
    this.getTypes();
    this.createForm();
    this.createFormSearch();
    this.loadData();

    this.auth.accessToken.profile.roles.forEach((role: UserRole) => {
      if (role.id === UserRoleType.ADMINISTRATOR) {
        this.isCurrentUserAdministrator = true;
      }
    });

    // uncomment this to make whispers work start
    /*
   join to broadcasting channel
    */

    /*
  if (this.websockets.isConnected()) {
    this.websockets
      .subscribeForComments(this.subjectType, this.subjectId, () => {
        this.loadData();
      })
      .subscribe((privateCommentsChannel: PusherPrivateChannel) => {
        this.websocketCommentChannel = privateCommentsChannel;
        privateCommentsChannel.listenForWhisper('typing', (data: any) => {
          const matches = this.usersTyping.filter((user: any, index: number) => {
            const match = user.id === data.id;
            if (match) {
              user.added = moment();
            }
            return match;
          });

          log.debug('matches', matches);
          if (matches.length === 0) {
            data.added = moment();
            this.usersTyping.push(data);
          }

          this.usersTyping = this.usersTyping.filter((user: any, index: number) => {
            return user.added.diff(moment().subtract(10, 'seconds')) > 0;
          });

          this.usersTypingTimeout = setTimeout(() => {
            this.clearWhisperUsers();
          }, 10000);

          log.debug('receive typing:', data, this.usersTyping);
        });

        this.keyUp
          .pipe(
            debounceTime(2000),
            distinctUntilChanged(),
            switchMap(event => of(event))
          )
          .subscribe(() => {
            log.debug('send typing');
            privateCommentsChannel.whisper('typing', {
              id: this.auth.accessToken.profile.id,
              fake_first_name: this.auth.accessToken.profile['fake_first_name'],
              fake_last_name: this.auth.accessToken.profile['fake_last_name']
            });
          });
      });
  }
*/

    // uncomment this to make whispers work end
  }

  // uncomment this to make whispers work start
  /*
  clearWhisperUsers() {
    log.debug('clearWhisperUsers');
    this.usersTyping = this.usersTyping.filter((user: any, index: number) => {
      return user.added.diff(moment().subtract(10, 'seconds')) > 0;
    });

    if (!this.usersTyping.length) {
      log.debug('clear clearWhisperUsers');
      clearTimeout(this.usersTypingTimeout);
    }

    log.debug('users after clearWhisperUsers', this.usersTyping);
  }

  whisper(event: KeyboardEvent) {
    this.keyUp.next(event);
  }
   */

  // uncomment this to make whispers work end

  postComment() {
    const comment = new CommentModel().deserialize(this.form.value);
    comment.color = comment.color.replace('#', '');
    this.commentsService.store(comment).subscribe((res) => {
      this.translate.get('COMMENTS.SUCCESS_STORE_MSG').subscribe((trans: string) => {
        this.toastr.success(trans);
        this.form.get('comment').reset();
        this.form.get('color').patchValue(this.commentDefaultColor);
        this.commentsService.hasValue = false;

        this.loadData(1);
      });
    });
  }

  paginate(event: any = null) {
    let page = 1;
    if (event && event.first > 0) {
      page = event.first / event.rows + 1;
    }

    this.loadData(page);
    this.perfectScroll.scrollTo({ top: 0, duration: 300 });
  }

  loadData(page: number = 1) {
    const apiQuery = new ApiQuery()
      .setPage(page)
      .addFilters({
        commentable_type: this.subjectType,
        commentable_id: this.subjectId,
      })
      .setLimit(50)
      .addIncludes('type', 'user.avatar');
    this.commentsService.index(apiQuery).subscribe((response: any) => {
      this.commentsResponse = response;
      this.perfectScroll.update();
    });
  }

  /**
   *  Показва коментара в textarea
   * @param comment comment
   */
  editComment(comment: CommentModel): void {
    comment.meta_front.edit = true;
  }

  /**
   * Expand comment
   * @param comment Comment
   */
  expandComment(comment: CommentModel): void {
    comment.meta_front.expand = !comment.meta_front.expand;
  }

  /**
   * Expand or collapse ALL comments
   */
  expandAll(): void {
    this.commentsExpanded = !this.commentsExpanded;
    this.commentsResponse.data.forEach((comment: CommentModel) => {
      comment.meta_front.expand = this.commentsExpanded;
    });
  }

  /**
   * Ъпдейтва коментара
   * @param comment comment
   */
  updateComment(comment: CommentModel): void {
    const commentCopy = new CommentModel().deserialize(comment);
    commentCopy.color = commentCopy.color.replace('#', '');

    this.commentsService.update(commentCopy).subscribe(
      () => {
        comment.meta_front.edit = false;
        this.loadData();

        this.translate.get('COMMENTS.SUCCESS_UPDATE_MSG').subscribe((trans: string) => {
          this.toastr.success(trans);
        });
      },
      (err) => {
        log.error(err);
        this.toastr.error('Update Subscribe fail ' + err);
      }
    );
  }

  /**
   * Изтрива коментар
   * @param comment comment
   */
  deleteComment(comment: CommentModel): void {
    this.commentsService.destroy(comment.id).subscribe(
      () => {
        this.loadData();
      },
      () => {}
    );
  }

  /**
   * On textarea change
   */
  onChange(): void {
    this.commentsService.hasValue = !!this.form.get('comment').value;
  }

  /**
   * Show/hide search bar
   */
  toggleSearch(): void {
    this.showSearch = !this.showSearch;
  }

  /**
   * Search comments for comment and types
   */
  search(): void {
    const apiQuery = new ApiQuery()
      .setPage(1)
      .addFilters({
        commentable_type: this.subjectType,
        commentable_id: this.subjectId,
        comment: this.formSearch.get('comment').value,
        type_id: this.formSearch.get('type_id').value,
      })
      .setLimit(50)
      .addIncludes('type', 'user.avatar');

    this.commentsService.index(apiQuery).subscribe((response: any) => {
      this.commentsResponse = response;
    });
  }

  /**
   * Clear the search form and results
   */
  clearSearch(): void {
    log.debug('search cleared!');
    this.formSearch.reset();
    this.selectedSearchType = null;
    this.loadData();
    this.toggleSearch();
  }

  /**
   * On search type change, change the selected comment type
   *
   * @param type Comment type
   */
  searchTypeChange(type: string | null): void {
    this.selectedSearchType = type;
  }

  private createForm(): void {
    this.form = this.formBuilder.group({
      id: [null],
      type_id: [null, [Validators.required]],
      comment: [null, [Validators.required]],
      commentable_id: [this.subjectId, [Validators.required]],
      commentable_type: [this.subjectType, [Validators.required]],
      search: [null],
      color: [this.commentDefaultColor],
    });
  }

  private createFormSearch(): void {
    this.formSearch = this.formBuilder.group({
      type_id: [null],
      comment: [''],
    });
  }

  private getTypes(): void {
    const apiQuery: ApiQuery = new ApiQuery();
    this.typesService
      .index(
        apiQuery
          .addFilters({
            type: 'comment',
          })
          .setLimit(9999)
      )
      .subscribe((res) => {
        log.debug('getTypes()');
        log.debug(res.data);
        this.types = res.data;
      });
  }

  private checkRequiredFields(input: any): void {
    if (input === null || input === undefined) {
      throw new Error('CommentsComponent Missing required attribute');
    }
  }
}
